/* MTH SMS balloon - a floating glyph that wanders the viewport and, on click,
   opens a popup with an embedded Klaviyo form.

   The flyer is a real <button>, so it is keyboard focusable and screen-reader
   friendly. Motion FREEZES on hover AND focus, so it is never a moving target
   for a mouse or keyboard user. Reduced-motion visitors get a static anchor.
   The Klaviyo form div is rendered up front (inside the hidden popup) so
   klaviyo.js hydrates it at page load; if it is still empty on first open we
   ask Klaviyo to re-scan. Dismiss/signup state persists in localStorage.

   ASCII-only, in its own asset file (never a {% javascript %} block): non-ASCII
   in a theme JS bundle has truncated scripts.js in this repo before. */

(function () {
  'use strict';

  var DISMISS_KEY = 'mth-balloon:dismissed';
  var SIGNUP_KEY = 'mth-balloon:signedup';
  var DAY_MS = 86400000;

  // Movement personalities for the velocity-steering engine. hop* are fractions
  // of the viewport diagonal (how far the next waypoint is); turn is the maximum
  // steering acceleration as a multiple of cruise speed (higher = sharper, more
  // darting turns); bob is a vertical sine overlay; tilt banks the glyph into its
  // turn. Speed is constant across a turn, so the glyph never stalls at a
  // waypoint the way the old per-segment easing did.
  var STYLES = {
    gentle: { hopMin: 0.3, hopMax: 0.55, turn: 2.2, bobAmp: 7, bobHz: 0.35, tiltMax: 8 },
    playful: { hopMin: 0.2, hopMax: 0.4, turn: 4.5, bobAmp: 11, bobHz: 0.7, tiltMax: 14 },
    frantic: { hopMin: 0.08, hopMax: 0.22, turn: 9.0, bobAmp: 4, bobHz: 2.0, tiltMax: 6 }
  };

  function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
  }

  function rand(lo, hi) {
    return lo + Math.random() * (hi - lo);
  }

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  class MthSmsBalloon extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this._readConfig();

      this.flyer = this.querySelector('.mth-sms-balloon__flyer');
      this.btn = this.querySelector('.mth-sms-balloon__btn');
      this.dismissBtn = this.querySelector('.mth-sms-balloon__dismiss');
      this.popup = this.querySelector('.mth-sms-balloon__popup');
      this.panel = this.querySelector('.mth-sms-balloon__panel');
      this.formDiv = this.querySelector('[class^="klaviyo-form-"]');
      // The simple (native Shopify) form does a full-page POST; on the reload
      // after a good submit, Liquid renders the success paragraph. Detecting it
      // is how we know the visitor just signed up via the simple form.
      this.postedSuccess = !!this.querySelector('.mth-sms-balloon__simple-success');

      // In popup mode there is no panel; we only require the flyer + button.
      if (!this.flyer || !this.btn || (!this.klaviyoPopup && !this.popup)) {
        this.remove();
        return;
      }

      // Suppressed by a prior dismiss/signup (skipped in test/design mode). We
      // never suppress on the success reload itself, so the visitor sees the
      // confirmation before the widget goes away.
      if (!this.postedSuccess && this._isSuppressed()) {
        this.remove();
        return;
      }

      this.frozen = false;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // Hoist the popup to <body> so its z-index (1200) competes at the top of
      // the page rather than being trapped inside this element's fixed/z-index
      // stacking context (which would let the cart drawer paint over it).
      if (this.popup) document.body.appendChild(this.popup);

      this._bind();

      // Simple-form success reload: mark signed up (so it stays hidden next
      // time), show the confirmation briefly, then clear it. No flight.
      if (this.postedSuccess) {
        this._setFlag(SIGNUP_KEY);
        this.hidden = false;
        this.classList.add('mth-sms-balloon--static');
        this._openPopup(true);
        this.successTimer = setTimeout(
          function () {
            this.remove();
          }.bind(this),
          6000
        );
        return;
      }

      var delay = this.testMode || isDesignMode() ? 0 : this.initialDelay;
      this.spawnTimer = setTimeout(this._spawn.bind(this), delay);
    }

    disconnectedCallback() {
      this._teardown();
    }

    _readConfig() {
      var d = this.dataset;
      this.testMode = d.testMode !== undefined;
      this.noMobile = d.noMobile !== undefined;
      this.entryEdge = d.entryEdge || 'random';
      this.speed = parseFloat(d.speed) || 120;
      this.style_ = STYLES[d.style] || STYLES.gentle;
      this.initialDelay = parseInt(d.initialDelay, 10) || 0;
      this.dismissDays = parseInt(d.dismissDays, 10);
      if (isNaN(this.dismissDays)) this.dismissDays = 7;
      this.signupHideDays = parseInt(d.signupHideDays, 10);
      if (isNaN(this.signupHideDays)) this.signupHideDays = 365;
      this.formId = d.formId || '';
      // Popup mode: the balloon click opens the Klaviyo popup via openForm
      // instead of showing our own panel; no popup markup is rendered.
      this.klaviyoPopup = d.klaviyoPopup !== undefined;
      // Hide-on-open: once the visitor opens the form, stop flying and stay gone
      // (governed by dismissDays), so it never becomes a moving nag after a close.
      this.hideOnOpen = d.hideOnOpen !== undefined;
    }

    // ---------------------------------------------------------------- Persistence ----------------------------------------------------------------

    _isSuppressed() {
      if (this.testMode || isDesignMode()) return false;
      return this._flagFresh(SIGNUP_KEY, this.signupHideDays) || this._flagFresh(DISMISS_KEY, this.dismissDays);
    }

    _setFlag(key) {
      try {
        localStorage.setItem(key, JSON.stringify({ at: Date.now() }));
      } catch (e) {}
    }

    _flagFresh(key, days) {
      if (!days) return false; // 0 days = never suppresses
      try {
        var raw = localStorage.getItem(key);
        if (!raw) return false;
        var at = JSON.parse(raw).at;
        return typeof at === 'number' && Date.now() - at < days * DAY_MS;
      } catch (e) {
        return false;
      }
    }

    // ---------------------------------------------------------------- Lifecycle ----------------------------------------------------------------

    _spawn() {
      if (!this.isConnected) return;
      this.hidden = false;
      if (this.reduced) {
        this.classList.add('mth-sms-balloon--static');
        return; // static anchor: no flight, click still works
      }
      this._launch();
    }

    _launch() {
      var size = this.flyer.offsetWidth || 90;
      this.flyerSize = size;
      this.hasSalePop = !!document.querySelector('mth-sale-pop');
      var vw = window.innerWidth;
      var vh = window.innerHeight;

      var edge = this.entryEdge;
      if (edge === 'random') {
        edge = ['left', 'right', 'top', 'bottom'][Math.floor(Math.random() * 4)];
      }

      // Start one glyph-size beyond the chosen edge, at a random offset along it.
      if (edge === 'left') {
        this.pos = { x: -size, y: rand(0, vh - size) };
      } else if (edge === 'right') {
        this.pos = { x: vw + size, y: rand(0, vh - size) };
      } else if (edge === 'top') {
        this.pos = { x: rand(0, vw - size), y: -size };
      } else {
        this.pos = { x: rand(0, vw - size), y: vh + size };
      }

      // Head straight for the first waypoint at cruise speed, so the glyph flies
      // in cleanly rather than easing up from a standstill.
      this.target = this._pickWaypoint();
      var toX = this.target.x - this.pos.x;
      var toY = this.target.y - this.pos.y;
      var d = Math.hypot(toX, toY) || 1;
      this.vel = { x: (toX / d) * this.speed, y: (toY / d) * this.speed };
      this.tilt = 0;
      this.entered = false;

      this.lastNow = performance.now();
      this._tickBound = this._tick.bind(this);
      this.raf = requestAnimationFrame(this._tickBound);
    }

    // ---------------------------------------------------------------- Flight engine ----------------------------------------------------------------

    _bounds() {
      var size = this.flyerSize;
      var mx = window.innerWidth * 0.08;
      var my = window.innerHeight * 0.08;
      var b = {
        minX: mx,
        minY: my,
        maxX: Math.max(mx, window.innerWidth - size - mx),
        maxY: Math.max(my, window.innerHeight - size - my)
      };
      // Keep waypoints clear of the sale-pop card (bottom-left) when it is
      // present, so the two floating widgets do not sit on top of each other.
      if (this.hasSalePop) {
        b.exclLeft = window.innerWidth * 0.5;
        b.exclTop = window.innerHeight - size - 120;
      }
      return b;
    }

    _pickWaypoint() {
      var b = this._bounds();
      var diag = Math.hypot(window.innerWidth, window.innerHeight);
      var hopLo = diag * this.style_.hopMin;
      var hopHi = diag * this.style_.hopMax;
      var cx = this.pos ? this.pos.x : (b.minX + b.maxX) / 2;
      var cy = this.pos ? this.pos.y : (b.minY + b.maxY) / 2;

      var pick = { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2 };
      for (var i = 0; i < 8; i++) {
        var x = rand(b.minX, b.maxX);
        var y = rand(b.minY, b.maxY);
        // Avoid the sale-pop exclusion zone if one is defined.
        if (b.exclLeft !== undefined && x < b.exclLeft && y > b.exclTop) continue;
        var dist = Math.hypot(x - cx, y - cy);
        pick = { x: x, y: y };
        if (dist >= hopLo && dist <= hopHi) break;
      }
      return pick;
    }

    _tick(now) {
      if (!this.isConnected) return;

      // dt in seconds, clamped so a long pause (hidden tab) can't teleport the
      // glyph on the frame it resumes.
      var dt = (now - this.lastNow) / 1000;
      this.lastNow = now;
      if (dt > 0.05) dt = 0.05;

      // Freeze in place on hover/focus/popup or while the tab is hidden. lastNow
      // is already advanced, so motion resumes with no jump.
      if (this.frozen || document.hidden) {
        this.raf = requestAnimationFrame(this._tickBound);
        return;
      }

      var speed = this.speed;
      var toX = this.target.x - this.pos.x;
      var toY = this.target.y - this.pos.y;
      var d = Math.hypot(toX, toY) || 1;

      // Steer velocity toward the target, capping how fast the direction can
      // change (maxSteer). Constant cruise speed + limited turn rate gives smooth
      // banking arcs with no stall at the waypoint.
      var desiredX = (toX / d) * speed;
      var desiredY = (toY / d) * speed;
      var steerX = desiredX - this.vel.x;
      var steerY = desiredY - this.vel.y;
      var maxSteer = this.style_.turn * speed * dt;
      var sMag = Math.hypot(steerX, steerY);
      if (sMag > maxSteer && sMag > 0) {
        steerX = (steerX / sMag) * maxSteer;
        steerY = (steerY / sMag) * maxSteer;
      }
      this.vel.x += steerX;
      this.vel.y += steerY;

      // Renormalize to a constant cruising speed.
      var vMag = Math.hypot(this.vel.x, this.vel.y) || 1;
      this.vel.x = (this.vel.x / vMag) * speed;
      this.vel.y = (this.vel.y / vMag) * speed;

      this.pos.x += this.vel.x * dt;
      this.pos.y += this.vel.y * dt;

      // Once fully in view, keep it inside the viewport (entry flew in from off
      // screen, so we only start clamping after it has arrived).
      var maxX = window.innerWidth - this.flyerSize;
      var maxY = window.innerHeight - this.flyerSize;
      if (!this.entered && this.pos.x >= 0 && this.pos.y >= 0 && this.pos.x <= maxX && this.pos.y <= maxY) {
        this.entered = true;
      }
      if (this.entered) {
        this.pos.x = clamp(this.pos.x, 0, maxX);
        this.pos.y = clamp(this.pos.y, 0, maxY);
      }

      // New waypoint once we arrive (generous radius so it flows past, not into it).
      var reach = Math.max(this.flyerSize, 48);
      if (d < reach) this.target = this._pickWaypoint();

      // Vertical bob overlay + bank tilt proportional to horizontal velocity, eased.
      var bob = this.style_.bobAmp * Math.sin((now / 1000) * this.style_.bobHz * 2 * Math.PI);
      var targetTilt = this.style_.tiltMax * (this.vel.x / speed);
      this.tilt += (targetTilt - this.tilt) * Math.min(1, dt * 4);

      this.flyer.style.transform =
        'translate3d(' + this.pos.x + 'px,' + (this.pos.y + bob) + 'px,0) rotate(' + this.tilt + 'deg)';

      this.raf = requestAnimationFrame(this._tickBound);
    }

    _onResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(
        function () {
          if (this.reduced || !this.pos || !this.target) return;
          var b = this._bounds();
          this.pos.x = clamp(this.pos.x, b.minX, b.maxX);
          this.pos.y = clamp(this.pos.y, b.minY, b.maxY);
          this.target.x = clamp(this.target.x, b.minX, b.maxX);
          this.target.y = clamp(this.target.y, b.minY, b.maxY);
        }.bind(this),
        150
      );
    }

    // ---------------------------------------------------------------- Interaction ----------------------------------------------------------------

    _bind() {
      // Wrap _openPopup so the click Event is not passed as its skipFocus
      // argument (which would be truthy and skip installing the focus trap on
      // every normal open).
      this._onOpen = function () {
        if (this.klaviyoPopup) {
          this._openKlaviyo();
        } else {
          this._openPopup();
        }
      }.bind(this);
      this._onFreeze = function () {
        this.frozen = true;
      }.bind(this);
      this._onUnfreeze = function () {
        if (!this._popupOpen) this.frozen = false;
      }.bind(this);
      this._onResizeB = this._onResize.bind(this);
      this._onKlaviyo = this._onKlaviyoForms.bind(this);
      this._onKeyup = function (e) {
        if (e.key === 'Escape' && this._popupOpen) this._closePopup();
      }.bind(this);
      // Stored so _teardown can remove them (a theme-editor re-render that reuses
      // the same node must not double-bind).
      this._onDismiss = this._dismiss.bind(this);
      this._onClose = this._closePopup.bind(this);

      this.btn.addEventListener('click', this._onOpen);
      this.btn.addEventListener('mouseenter', this._onFreeze);
      this.btn.addEventListener('mouseleave', this._onUnfreeze);
      this.btn.addEventListener('focusin', this._onFreeze);
      this.btn.addEventListener('focusout', this._onUnfreeze);

      if (this.dismissBtn) {
        this.dismissBtn.addEventListener('click', this._onDismiss);
      }

      this._closers = this.popup ? this.popup.querySelectorAll('[data-close]') : [];
      for (var i = 0; i < this._closers.length; i++) {
        this._closers[i].addEventListener('click', this._onClose);
      }

      window.addEventListener('resize', this._onResizeB);
      document.addEventListener('keyup', this._onKeyup);
      document.addEventListener('klaviyoForms', this._onKlaviyo);
    }

    _openPopup(skipFocus) {
      this._popupOpen = true;
      this.frozen = true;
      this.popup.hidden = false;
      document.body.classList.add('overflow-hidden');

      // If klaviyo.js scanned before this div existed (or has not hydrated it),
      // ask it to re-scan the page for embed divs.
      if (this.formDiv && !this.formDiv.children.length) {
        window._klOnsite = window._klOnsite || [];
        window._klOnsite.push(['rerenderForms']);
      }

      // hideOnOpen: treat a genuine open as a dismissal and retire the balloon
      // when the panel closes, so it never resumes flying after a close. Skipped
      // for the success auto-open (skipFocus), which manages its own removal.
      if (this.hideOnOpen && !skipFocus) {
        this._setFlag(DISMISS_KEY);
        this._removeOnClose = true;
      }

      // skipFocus is used for the auto-opened success confirmation, so we don't
      // yank focus into a trap on an unprompted page load.
      if (skipFocus) return;

      if (typeof trapFocus === 'function') {
        trapFocus(this.panel);
      } else {
        var close = this.popup.querySelector('.mth-sms-balloon__popup-close');
        if (close) close.focus();
      }
    }

    _closePopup() {
      this._popupOpen = false;
      this.popup.hidden = true;
      document.body.classList.remove('overflow-hidden');
      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.btn);
      }
      this.frozen = false;
      // hideOnOpen: the visitor has had their look; retire the balloon instead of
      // resuming flight. remove() -> disconnectedCallback -> _teardown does cleanup.
      if (this._removeOnClose) this.remove();
    }

    _dismiss() {
      this._setFlag(DISMISS_KEY);
      // remove() fires disconnectedCallback -> _teardown, which does all cleanup.
      this.remove();
    }

    // ---------------------------------------------------------------- Klaviyo ----------------------------------------------------------------

    // Popup mode: open the Klaviyo popup/flyout form by ID. Same contract as
    // assets/mth-notify.js: call the API if klaviyo.js has hydrated, otherwise
    // queue on _klOnsite so it opens once the app embed loads. Submit/dismiss
    // suppression still flows through the document 'klaviyoForms' event below.
    _openKlaviyo() {
      var id = this.formId;
      if (!id) return;
      if (window.klaviyo && typeof window.klaviyo.openForm === 'function') {
        window.klaviyo.openForm(id);
      } else {
        window._klOnsite = window._klOnsite || [];
        window._klOnsite.push(['openForm', id]);
      }
      this._retireOnOpen();
    }

    // Once opened, freeze + hide the flyer and record a dismissal so it stays
    // gone for dismissDays. The element (and its klaviyoForms listener) stays in
    // the DOM so a later submit can still upgrade the suppression to signupHide
    // days and fully remove it. No-op unless hideOnOpen is set.
    _retireOnOpen() {
      if (!this.hideOnOpen) return;
      this._setFlag(DISMISS_KEY);
      this.frozen = true;
      this.hidden = true;
      cancelAnimationFrame(this.raf);
    }

    _onKlaviyoForms(e) {
      if (!e || !e.detail) return;
      // Ignore events from other Klaviyo forms on the page.
      if (e.detail.formId && this.formId && e.detail.formId !== this.formId) return;

      if (e.detail.type === 'submit') {
        this._setFlag(SIGNUP_KEY);
        // Let Klaviyo show its success step, then remove the widget (teardown
        // via disconnectedCallback restores body scroll and un-hoists the popup).
        this.submitTimer = setTimeout(
          function () {
            this.remove();
          }.bind(this),
          2500
        );
      } else if (e.detail.type === 'stepSubmit') {
        // Multi-step forms may never fire 'submit'; soft-suppress so we don't nag.
        this._setFlag(DISMISS_KEY);
      }
    }

    // ---------------------------------------------------------------- Teardown ----------------------------------------------------------------

    _teardown() {
      cancelAnimationFrame(this.raf);
      clearTimeout(this.spawnTimer);
      clearTimeout(this.resizeTimer);
      clearTimeout(this.successTimer);
      clearTimeout(this.submitTimer);
      window.removeEventListener('resize', this._onResizeB);
      document.removeEventListener('keyup', this._onKeyup);
      document.removeEventListener('klaviyoForms', this._onKlaviyo);
      // Remove element-level listeners too, so a theme-editor re-render that
      // reuses the same node cannot double-bind them.
      if (this.btn) {
        this.btn.removeEventListener('click', this._onOpen);
        this.btn.removeEventListener('mouseenter', this._onFreeze);
        this.btn.removeEventListener('mouseleave', this._onUnfreeze);
        this.btn.removeEventListener('focusin', this._onFreeze);
        this.btn.removeEventListener('focusout', this._onUnfreeze);
      }
      if (this.dismissBtn) this.dismissBtn.removeEventListener('click', this._onDismiss);
      if (this._closers) {
        for (var i = 0; i < this._closers.length; i++) {
          this._closers[i].removeEventListener('click', this._onClose);
        }
      }
      // The popup was hoisted to <body>; move it back inside this element so it
      // is not orphaned in <body> after the element leaves the DOM (and so a
      // reconnect of the same node can re-find and re-hoist it).
      if (this.popup) this.appendChild(this.popup);
      document.body.classList.remove('overflow-hidden');
      // Allow a genuine reconnect (e.g. a drag-reorder that re-parents the node)
      // to re-initialize instead of staying permanently dead.
      this._init = false;
    }
  }

  customElements.define('mth-sms-balloon', MthSmsBalloon);
})();
